import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Activity, 
  Target, 
  Clock, 
  Users, 
  Bot, 
  Brain, 
  Zap,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Star,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { cn } from '../../../utils/cn';

interface ConversationAnalyticsData {
  aiAccuracy: number;
  aiConfidenceScore: number;
  aiResponseSuccessRate: number;
  automationCoverage: number;
  totalConversations: number;
  activeConversations: number;
  averageResponseTime: number;
  conversionRate: number;
}

interface PerformanceMetrics {
  responseTimeDistribution: Array<{
    range: string;
    count: number;
    percentage: number;
  }>;
  sentimentTrends: Array<{
    date: string;
    positive: number;
    neutral: number;
    negative: number;
  }>;
  intentDistribution: Array<{
    intent: string;
    count: number;
    percentage: number;
  }>;
  channelPerformance: Array<{
    channel: string;
    totalConversations: number;
    conversionRate: number;
    escalationRate: number;
    avgResponseTime: number;
  }>;
}

interface ConversationAnalyticsProps {
  analytics: ConversationAnalyticsData | null;
  performanceMetrics: PerformanceMetrics | null;
}

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'up' | 'down' | 'neutral';
  icon: React.ElementType;
  color: string;
  description?: string;
}> = ({ title, value, change, changeType, icon: Icon, color, description }) => {
  const ChangeIcon = changeType === 'up' ? ArrowUp : changeType === 'down' ? ArrowDown : ArrowRight;
  const changeColor = changeType === 'up' ? 'text-green-400' : changeType === 'down' ? 'text-red-400' : 'text-yellow-400';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-4 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 backdrop-blur-xl border border-neutral-700/50 rounded-xl hover:border-neutral-600/50 transition-all duration-300"
    >
      <div className="flex items-center justify-between mb-3">
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", color)}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {change && (
          <span className={cn("text-xs font-medium flex items-center", changeColor)}>
            <ChangeIcon className="w-3 h-3 mr-1" />
            {change}
          </span>
        )}
      </div>
      <div>
        <p className="text-neutral-400 text-xs font-medium mb-1">{title}</p>
        <p className="text-2xl font-bold text-white mb-1">{value}</p>
        {description && (
          <p className="text-neutral-500 text-xs">{description}</p>
        )}
      </div>
    </motion.div>
  );
};

const ProgressBar: React.FC<{
  label: string;
  value: number;
  maxValue: number;
  color: string;
  showPercentage?: boolean;
}> = ({ label, value, maxValue, color, showPercentage = true }) => {
  const percentage = Math.round((value / maxValue) * 100);
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-neutral-300 text-sm">{label}</span>
        <span className="text-neutral-400 text-sm">
          {showPercentage ? `${percentage}%` : value}
        </span>
      </div>
      <div className="w-full bg-neutral-700 rounded-full h-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={cn("h-2 rounded-full", color)}
        />
      </div>
    </div>
  );
};

const TrendChart: React.FC<{
  title: string;
  data: Array<{ date: string; positive: number; neutral: number; negative: number; }>;
}> = ({ title, data }) => {
  const maxValue = Math.max(...data.flatMap(d => [d.positive, d.neutral, d.negative]));
  
  return (
    <div className="p-4 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 backdrop-blur-xl border border-neutral-700/50 rounded-xl">
      <h4 className="text-white font-medium mb-4 flex items-center">
        <TrendingUp className="w-4 h-4 mr-2 text-blue-400" />
        {title}
      </h4>
      <div className="space-y-3">
        {data.slice(-7).map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-neutral-400 text-xs">
                {new Date(item.date).toLocaleDateString()}
              </span>
              <div className="flex space-x-2 text-xs">
                <span className="text-green-400">{item.positive}%</span>
                <span className="text-yellow-400">{item.neutral}%</span>
                <span className="text-red-400">{item.negative}%</span>
              </div>
            </div>
            <div className="flex space-x-1 h-2">
              <div 
                className="bg-green-500 rounded-l-full"
                style={{ width: `${(item.positive / 100) * 100}%` }}
              />
              <div 
                className="bg-yellow-500"
                style={{ width: `${(item.neutral / 100) * 100}%` }}
              />
              <div 
                className="bg-red-500 rounded-r-full"
                style={{ width: `${(item.negative / 100) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChannelPerformanceCard: React.FC<{
  channel: string;
  data: {
    totalConversations: number;
    conversionRate: number;
    escalationRate: number;
    avgResponseTime: number;
  };
}> = ({ channel, data }) => {
  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'sms': return MessageSquare;
      case 'email': return MessageSquare;
      case 'phone': return MessageSquare;
      default: return MessageSquare;
    }
  };

  const ChannelIcon = getChannelIcon(channel);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 backdrop-blur-xl border border-neutral-700/50 rounded-xl hover:border-blue-500/30 transition-all duration-300"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <ChannelIcon className="w-4 h-4 text-blue-400" />
          <span className="text-white font-medium capitalize">{channel}</span>
        </div>
        <span className="text-neutral-400 text-sm">{data.totalConversations} total</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-neutral-400 text-xs mb-1">Conversion</p>
          <p className="text-green-400 font-semibold">{data.conversionRate}%</p>
        </div>
        <div>
          <p className="text-neutral-400 text-xs mb-1">Escalation</p>
          <p className="text-red-400 font-semibold">{data.escalationRate}%</p>
        </div>
        <div className="col-span-2">
          <p className="text-neutral-400 text-xs mb-1">Avg Response Time</p>
          <p className="text-blue-400 font-semibold">{data.avgResponseTime}s</p>
        </div>
      </div>
    </motion.div>
  );
};

export const ConversationAnalytics: React.FC<ConversationAnalyticsProps> = ({
  analytics,
  performanceMetrics
}) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('overview');

  if (!analytics && !performanceMetrics) {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-neutral-400">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
          Performance Analytics
        </h3>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-1 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        >
          <option value="1d">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* AI Performance Metrics */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="AI Accuracy"
            value={`${analytics.aiConfidenceScore || 0}%`}
            change="+5% vs last week"
            changeType="up"
            icon={Brain}
            color="bg-gradient-to-r from-cyan-500 to-blue-500"
            description="AI classification accuracy"
          />
          <MetricCard
            title="Automation Coverage"
            value={`${analytics.automationCoverage || 0}%`}
            change="+12% vs last week"
            changeType="up"
            icon={Bot}
            color="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Conversations handled by AI"
          />
          <MetricCard
            title="Response Success Rate"
            value={`${analytics.aiResponseSuccessRate || 0}%`}
            change="+3% vs last week"
            changeType="up"
            icon={Target}
            color="bg-gradient-to-r from-purple-500 to-pink-500"
            description="AI responses leading to positive outcomes"
          />
          <MetricCard
            title="Avg Response Time"
            value={`${analytics.averageResponseTime || 0}s`}
            change="-15% vs last week"
            changeType="down"
            icon={Zap}
            color="bg-gradient-to-r from-yellow-500 to-orange-500"
            description="AI-accelerated response speed"
          />
        </div>
      )}

      {/* Response Time Distribution */}
      {performanceMetrics?.responseTimeDistribution && (
        <div className="p-4 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 backdrop-blur-xl border border-neutral-700/50 rounded-xl">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <Clock className="w-4 h-4 mr-2 text-green-400" />
            Response Time Distribution
          </h4>
          <div className="space-y-3">
            {performanceMetrics.responseTimeDistribution.map((item, index) => (
              <ProgressBar
                key={index}
                label={item.range}
                value={item.count}
                maxValue={Math.max(...performanceMetrics.responseTimeDistribution.map(d => d.count))}
                color="bg-gradient-to-r from-green-500 to-emerald-500"
              />
            ))}
          </div>
        </div>
      )}

      {/* Sentiment Trends */}
      {performanceMetrics?.sentimentTrends && (
        <TrendChart
          title="Sentiment Trends"
          data={performanceMetrics.sentimentTrends}
        />
      )}

      {/* Intent Distribution */}
      {performanceMetrics?.intentDistribution && (
        <div className="p-4 bg-gradient-to-br from-neutral-900/50 to-neutral-800/30 backdrop-blur-xl border border-neutral-700/50 rounded-xl">
          <h4 className="text-white font-medium mb-4 flex items-center">
            <Target className="w-4 h-4 mr-2 text-purple-400" />
            Intent Distribution
          </h4>
          <div className="space-y-3">
            {performanceMetrics.intentDistribution.slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-neutral-300 text-sm capitalize">{item.intent}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-neutral-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.percentage}%` }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                    />
                  </div>
                  <span className="text-neutral-400 text-sm w-8">{item.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Channel Performance */}
      {performanceMetrics?.channelPerformance && (
        <div>
          <h4 className="text-white font-medium mb-4 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-blue-400" />
            Channel Performance
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performanceMetrics.channelPerformance.map((channel, index) => (
              <ChannelPerformanceCard
                key={index}
                channel={channel.channel}
                data={channel}
              />
            ))}
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-gradient-to-br from-green-900/20 to-green-800/20 border border-green-500/30 rounded-xl">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-green-300 font-semibold">High Performance</p>
              <p className="text-green-400 text-sm">AI handling 85%+ conversations</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-gradient-to-br from-blue-900/20 to-blue-800/20 border border-blue-500/30 rounded-xl">
          <div className="flex items-center space-x-3">
            <Sparkles className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-blue-300 font-semibold">AI Optimized</p>
              <p className="text-blue-400 text-sm">Response times under 30s</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-gradient-to-br from-purple-900/20 to-purple-800/20 border border-purple-500/30 rounded-xl">
          <div className="flex items-center space-x-3">
            <Star className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-purple-300 font-semibold">Quality Assured</p>
              <p className="text-purple-400 text-sm">95%+ confidence scores</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 